import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { adminKeys } from "@/lib/query";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { dateSortValue, formatDate, formatInteger, formatMoney } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import { AdminDataTable, type AdminDataTableColumn } from "@/components/admin/AdminDataTable";

type TierSales = { name: string; count: number; grossCents: number };

type RetreatSalesRow = {
  retreatTitle: string;
  status: string;
  capacity: number | null;
  soldSeats: number;
  grossCents: number;
  tiers: TierSales[];
  vipCount: number;
  vipGrossCents: number;
};

type ProductSalesRow = {
  name: string;
  slug: string;
  unitsSold: number;
  grossCents: number;
};

type SalesRow = {
  id: string;
  source: "retreat" | "shop";
  lineType: "retreat-registration" | "retreat-addon" | "shop-product";
  date: string;
  customerName: string;
  customerEmail: string;
  retreatTitle: string | null;
  retreatSlug: string | null;
  productName: string | null;
  productSlug: string | null;
  itemName: string;
  quantity: number;
  grossCents: number;
  status: string;
};

type SalesData = {
  salesRows: SalesRow[];
  retreatSales: RetreatSalesRow[];
  productSales: ProductSalesRow[];
  retreatGrossCents: number;
  shopGrossCents: number;
};

type SalesSortKey = "date" | "source" | "entity" | "item" | "customer" | "quantity" | "status" | "revenue";

function entityLabel(row: SalesRow) {
  return row.source === "retreat" ? row.retreatTitle ?? "Unknown retreat" : row.productName ?? "Unknown product";
}

function searchText(row: SalesRow) {
  return [
    row.source,
    row.lineType,
    entityLabel(row),
    row.retreatSlug,
    row.productSlug,
    row.itemName,
    row.customerName,
    row.customerEmail,
    row.status,
    formatMoney(row.grossCents),
  ]
    .filter(Boolean)
    .join(" ");
}

const salesSorters: Record<SalesSortKey, (row: SalesRow) => string | number> = {
  date: (row) => dateSortValue(row.date),
  source: (row) => row.source,
  entity: entityLabel,
  item: (row) => row.itemName,
  customer: (row) => row.customerName || row.customerEmail,
  quantity: (row) => row.quantity,
  status: (row) => row.status,
  revenue: (row) => row.grossCents,
};

export default function AdminSales() {
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const salesQuery = useQuery({
    queryKey: adminKeys.sales(),
    queryFn: () => api.get<SalesData>("/api/admin/sales"),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const data = salesQuery.data ?? null;

  const rows = data?.salesRows ?? [];
  const entityOptions = useMemo(
    () =>
      ["all", ...Array.from(new Set(rows.map(entityLabel))).sort((a, b) => a.localeCompare(b))].map((value) => ({
        value,
        label: value === "all" ? "All products/events/retreats" : value,
      })),
    [rows],
  );
  const statusOptions = useMemo(
    () =>
      ["all", ...Array.from(new Set(rows.map((row) => row.status))).sort()].map((value) => ({
        value,
        label: value === "all" ? "All statuses" : value,
      })),
    [rows],
  );
  const filters = useMemo(
    () => [
      (row: SalesRow) => sourceFilter === "all" || row.source === sourceFilter,
      (row: SalesRow) => entityFilter === "all" || entityLabel(row) === entityFilter,
      (row: SalesRow) => statusFilter === "all" || row.status === statusFilter,
    ],
    [entityFilter, sourceFilter, statusFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<SalesRow, SalesSortKey>({
    rows,
    search,
    initialSort: { key: "date", direction: "desc" },
    sorters: salesSorters,
    searchText,
    filters,
  });

  const filteredRevenue = tableRows.reduce((sum, row) => sum + row.grossCents, 0);
  const filteredUnits = tableRows.reduce((sum, row) => sum + row.quantity, 0);
  const apiBase = (import.meta.env.VITE_API_URL ?? "") as string;
  const columns: Array<AdminDataTableColumn<SalesRow, SalesSortKey>> = [
    {
      id: "date",
      header: "Date",
      sortKey: "date",
      width: "8.5rem",
      nowrap: true,
      render: (row) => formatDate(row.date),
    },
    {
      id: "source",
      header: "Source",
      sortKey: "source",
      width: "7.5rem",
      render: (row) => <span className={`source-pill source-${row.source}`}>{row.source}</span>,
    },
    {
      id: "entity",
      header: "Product / event / retreat",
      sortKey: "entity",
      width: "18rem",
      render: (row) => (
        <>
          <strong>{entityLabel(row)}</strong>
          {(row.retreatSlug || row.productSlug) && (
            <span className="sub">{row.retreatSlug ?? row.productSlug}</span>
          )}
        </>
      ),
    },
    {
      id: "item",
      header: "Line item",
      sortKey: "item",
      width: "16rem",
      render: (row) => (
        <>
          {row.itemName}
          <span className="sub">{row.lineType.replace(/-/g, " ")}</span>
        </>
      ),
    },
    {
      id: "customer",
      header: "Customer",
      sortKey: "customer",
      width: "18rem",
      render: (row) => (
        <>
          <strong>{row.customerName}</strong>
          <span className="sub">{row.customerEmail}</span>
        </>
      ),
    },
    {
      id: "quantity",
      header: "Qty",
      sortKey: "quantity",
      width: "5rem",
      render: (row) => formatInteger(row.quantity),
    },
    { id: "status", header: "Status", sortKey: "status", width: "9rem", render: (row) => row.status },
    {
      id: "revenue",
      header: "Revenue",
      sortKey: "revenue",
      width: "8rem",
      nowrap: true,
      render: (row) => formatMoney(row.grossCents),
    },
  ];

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Sales</h1>
        <a className="btn-ghost" href={`${apiBase}/api/admin/sales/sales.csv`} download>
          Download CSV
        </a>
      </div>

      {salesQuery.isLoading ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading...</p>
      ) : !data ? (
        <p style={{ fontFamily: fonts.body, color: colors.berryRose }}>Failed to load sales data.</p>
      ) : (
        <>
          <div className="cards-row">
            <div className="paper-card">
              <div className="card-label">Retreat revenue</div>
              <div className="card-value">{formatMoney(data.retreatGrossCents)}</div>
            </div>
            <div className="paper-card">
              <div className="card-label">Shop & event revenue</div>
              <div className="card-value">{formatMoney(data.shopGrossCents)}</div>
            </div>
            <div className="paper-card">
              <div className="card-label">Sales rows</div>
              <div className="card-value">{formatInteger(rows.length)}</div>
            </div>
          </div>

          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search sales, customers, products, retreats..."
            totalCount={rows.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Source"
              value={sourceFilter}
              onChange={setSourceFilter}
              options={[
                { value: "all", label: "All sources" },
                { value: "retreat", label: "Retreats" },
                { value: "shop", label: "Shop" },
              ]}
            />
            <AdminTableFilterSelect
              label="Product/event/retreat"
              value={entityFilter}
              onChange={setEntityFilter}
              options={entityOptions}
            />
            <AdminTableFilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
            />
          </AdminTableControls>

          <div className="cards-row" aria-label="Filtered sales totals">
            <div className="paper-card"><div className="card-label">Selected revenue</div><div className="card-value">{formatMoney(filteredRevenue)}</div></div>
            <div className="paper-card"><div className="card-label">Selected units / tickets</div><div className="card-value">{formatInteger(filteredUnits)}</div></div>
          </div>

          {rows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No sales yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No sales match those filters.</p>
          ) : (
            <AdminDataTable
              columns={columns}
              rows={tableRows}
              getRowKey={(row) => row.id}
              sort={sort}
              onSort={requestSort}
              minWidth={1160}
            />
          )}

          <p className="footnote">
            Revenue = amount actually collected. Retreat deposits count at their paid amount, balance
            payments are included after Stripe confirms them, and add-ons are separate rows. Shop and event totals include discounts, tax, and shipping, allocated across their line items. Stripe processing fees are not deducted.
          </p>
        </>
      )}
    </section>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }

  .cards-row { display: flex; gap: 1rem; margin-bottom: 2rem; flex-wrap: wrap; }
  .paper-card {
    background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px;
    padding: 1.1rem 1.5rem; min-width: 180px;
  }
  .card-label {
    font-family: ${fonts.body}; font-size: 10px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.18em; color: rgba(8,45,61,0.6); margin-bottom: 0.4rem;
  }
  .card-value {
    font-family: ${fonts.logo}; font-size: 1.8rem; font-weight: 500; color: ${colors.navy};
  }

  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; text-decoration: none; display: inline-block;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }

  .admin-data-table .sub { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }
  .source-pill { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; font-family: ${fonts.body}; }
  .source-retreat { background: rgba(100,149,237,0.15); color: #3155a0; }
  .source-shop { background: rgba(60,179,113,0.15); color: #2d7a52; }

  .footnote { font-family: ${fonts.body}; font-size: 0.75rem; color: rgba(8,45,61,0.5); margin-top: 1.5rem; }
`;
